/**
 * ESRS S4 — Consumers & End-Users.
 * Product incidents, consumer complaints, and recalls affecting end-user
 * safety and rights. Backend: backend/routes/esrs_s4_routes.py (/api/esrs/s4).
 * Chrome is EsgKit only; DESIGN.md tokens as inline styles, data in mono,
 * status always color + icon + label.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ShoppingCart, ShieldAlert, Lock, AlertTriangle, Package, MessageSquare,
  Plus, CheckCircle2, Clock, BadgeCheck,
} from "lucide-react";
import {
  Page, PageHeader, Card, Overline, Metric, Badge, Data, Btn, TabBar,
  DataTable, EmptyState, ErrorBanner, Skeleton, field, Label, apiFetch, fmt,
  C, SANS,
} from "../../../components/esg/EsgKit";
import { useEntities } from "../../../hooks/useEntities";

// Backend enums (models/esrs_social.py) — the route validates against these.
const INCIDENT_TYPES = [
  "safety", "recall", "data_privacy", "accessibility", "mislabeling",
];
const SEVERITIES = ["critical", "high", "medium", "low", "negligible"];
const COMPLAINT_STATUSES = [
  "identified", "planned", "in_progress", "completed", "verified",
  "closed", "ineffective",
];
const CHANNELS = [
  "hotline", "email", "web_form", "retailer", "regulator", "social_media",
  "other",
];

const truncate = (s, n = 64) =>
  !s ? "—" : s.length > n ? `${s.slice(0, n - 1)}…` : s;

const labelize = (s) =>
  !s ? "—" : s.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

// ─── Incident type badge ───────────────────────────────────────────────────
function IncidentTypeBadge({ type }) {
  const map = {
    safety: { c: C.destructive, Icon: ShieldAlert },
    recall: { c: C.destructive, Icon: ShieldAlert },
    data_privacy: { c: C.locked, Icon: Lock },
  };
  const { c, Icon } = map[type] || { c: C.neutral, Icon: AlertTriangle };
  return <Badge c={c} Icon={Icon} label={labelize(type)} />;
}

// ─── Severity badge ────────────────────────────────────────────────────────
function SeverityBadge({ severity }) {
  const c =
    severity === "critical" || severity === "high"
      ? C.destructive
      : severity === "medium"
      ? C.accent
      : C.neutral;
  return <Badge c={c} Icon={AlertTriangle} label={labelize(severity)} />;
}

// ─── Complaint status badge ────────────────────────────────────────────────
function ComplaintStatusBadge({ status }) {
  const map = {
    completed: { c: C.primary, Icon: CheckCircle2 },
    verified: { c: C.locked, Icon: CheckCircle2 },
    closed: { c: C.primary, Icon: CheckCircle2 },
    in_progress: { c: C.accent, Icon: Clock },
    ineffective: { c: C.destructive, Icon: AlertTriangle },
  };
  const { c, Icon } = map[status] || { c: C.neutral, Icon: MessageSquare };
  return <Badge c={c} Icon={Icon} label={labelize(status)} />;
}

// ─── Collapsible form shell ────────────────────────────────────────────────
function Collapsible({ open, onToggle, openLabel, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <Btn variant="secondary" Icon={Plus} onClick={onToggle}>
        {open ? "Cancel" : openLabel}
      </Btn>
      {open && (
        <div
          style={{
            marginTop: 12,
            padding: 16,
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            background: C.surfaceHi,
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

const grid2 = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
  gap: 12,
  marginBottom: 12,
};

const checkRow = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontFamily: SANS,
  fontSize: 13,
  color: C.body,
  marginBottom: 12,
  cursor: "pointer",
};

export default function S4Consumers() {
  const { entities, entityId, setEntityId } = useEntities("esrs_s4_entity_id");

  const [summary, setSummary] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [recalls, setRecalls] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("incidents");

  // Filters
  const [incType, setIncType] = useState("");
  const [incSeverity, setIncSeverity] = useState("");
  const [cmpStatus, setCmpStatus] = useState("");

  // Forms
  const [incOpen, setIncOpen] = useState(false);
  const [cmpOpen, setCmpOpen] = useState(false);
  const [rclOpen, setRclOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [incForm, setIncForm] = useState({
    product_name: "",
    incident_type: INCIDENT_TYPES[0],
    severity: SEVERITIES[0],
    occurred_date: "",
    affected_consumers_count: "",
    regulator_reported: false,
    description: "",
  });
  const [cmpForm, setCmpForm] = useState({
    channel: CHANNELS[0],
    received_date: "",
    product_name: "",
    description: "",
  });
  const [rclForm, setRclForm] = useState({
    product_name: "",
    initiated_date: "",
    reason: "",
    units_affected: "",
    incident_id: "",
    completed: false,
  });

  // Per-row complaint status editor
  const [statusEdit, setStatusEdit] = useState({}); // id -> {status, resolved_date}

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    setError("");
    try {
      const incQ = new URLSearchParams({ entity_id: entityId });
      if (incType) incQ.set("incident_type", incType);
      if (incSeverity) incQ.set("severity", incSeverity);
      const cmpQ = new URLSearchParams({ entity_id: entityId });
      if (cmpStatus) cmpQ.set("status", cmpStatus);

      const [s, inc, cmp, rcl] = await Promise.all([
        apiFetch(`/api/esrs/s4/summary?entity_id=${encodeURIComponent(entityId)}`),
        apiFetch(`/api/esrs/s4/incidents?${incQ.toString()}`),
        apiFetch(`/api/esrs/s4/complaints?${cmpQ.toString()}`),
        apiFetch(`/api/esrs/s4/recalls?entity_id=${encodeURIComponent(entityId)}`),
      ]);
      setSummary(s);
      setIncidents(inc?.incidents || []);
      setComplaints(cmp?.complaints || []);
      setRecalls(rcl?.recalls || []);
    } catch (e) {
      setError(e.message || "Failed to load S4 data");
    } finally {
      setLoading(false);
    }
  }, [entityId, incType, incSeverity, cmpStatus]);

  useEffect(() => {
    load();
  }, [load]);

  // ─── Derived KPIs ────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const bySeverity = summary?.incidents?.by_severity || {};
    const criticalHigh =
      (bySeverity.critical || 0) + (bySeverity.high || 0);
    const activeRecalls =
      (summary?.recalls?.total || 0) - (summary?.recalls?.completed || 0);
    return {
      totalIncidents: summary?.incidents?.total ?? 0,
      criticalHigh,
      openComplaints: summary?.complaints?.open ?? 0,
      activeRecalls,
    };
  }, [summary]);

  // ─── Mutations ───────────────────────────────────────────────────────────
  async function submitIncident(e) {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await apiFetch("/api/esrs/s4/incidents", {
        method: "POST",
        body: {
          entity_id: entityId,
          product_name: incForm.product_name,
          incident_type: incForm.incident_type,
          severity: incForm.severity,
          occurred_date: incForm.occurred_date,
          affected_consumers_count:
            incForm.affected_consumers_count === ""
              ? 0
              : Number(incForm.affected_consumers_count),
          regulator_reported: incForm.regulator_reported,
          description: incForm.description,
        },
      });
      setIncOpen(false);
      setIncForm({
        product_name: "",
        incident_type: INCIDENT_TYPES[0],
        severity: SEVERITIES[0],
        occurred_date: "",
        affected_consumers_count: "",
        regulator_reported: false,
        description: "",
      });
      await load();
    } catch (err) {
      setError(err.message || "Failed to record incident");
    } finally {
      setSaving(false);
    }
  }

  async function submitComplaint(e) {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await apiFetch("/api/esrs/s4/complaints", {
        method: "POST",
        body: {
          entity_id: entityId,
          channel: cmpForm.channel,
          received_date: cmpForm.received_date,
          product_name: cmpForm.product_name,
          description: cmpForm.description,
        },
      });
      setCmpOpen(false);
      setCmpForm({
        channel: CHANNELS[0],
        received_date: "",
        product_name: "",
        description: "",
      });
      await load();
    } catch (err) {
      setError(err.message || "Failed to record complaint");
    } finally {
      setSaving(false);
    }
  }

  async function submitRecall(e) {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await apiFetch("/api/esrs/s4/recalls", {
        method: "POST",
        body: {
          entity_id: entityId,
          product_name: rclForm.product_name,
          initiated_date: rclForm.initiated_date,
          reason: rclForm.reason,
          units_affected:
            rclForm.units_affected === ""
              ? 0
              : Number(rclForm.units_affected),
          incident_id: rclForm.incident_id || null,
          completed: rclForm.completed,
        },
      });
      setRclOpen(false);
      setRclForm({
        product_name: "",
        initiated_date: "",
        reason: "",
        units_affected: "",
        incident_id: "",
        completed: false,
      });
      await load();
    } catch (err) {
      setError(err.message || "Failed to record recall");
    } finally {
      setSaving(false);
    }
  }

  async function submitStatus(complaintId) {
    const edit = statusEdit[complaintId];
    if (!edit?.status) return;
    setSaving(true);
    setError("");
    try {
      await apiFetch(
        `/api/esrs/s4/complaints/${encodeURIComponent(complaintId)}/status`,
        {
          method: "POST",
          body: {
            status: edit.status,
            ...(edit.resolved_date ? { resolved_date: edit.resolved_date } : {}),
          },
        }
      );
      setStatusEdit((s) => {
        const next = { ...s };
        delete next[complaintId];
        return next;
      });
      await load();
    } catch (err) {
      setError(err.message || "Failed to update status");
    } finally {
      setSaving(false);
    }
  }

  // ─── Table column defs ───────────────────────────────────────────────────
  const incidentCols = [
    {
      key: "product_name",
      label: "Product",
      render: (r) => <Data color={C.ink}>{r.product_name || "—"}</Data>,
    },
    {
      key: "incident_type",
      label: "Type",
      render: (r) => <IncidentTypeBadge type={r.incident_type} />,
    },
    {
      key: "severity",
      label: "Severity",
      render: (r) => <SeverityBadge severity={r.severity} />,
    },
    {
      key: "occurred_date",
      label: "Occurred",
      render: (r) => <Data color={C.body}>{r.occurred_date || "—"}</Data>,
    },
    {
      key: "affected_consumers_count",
      label: "Affected",
      align: "right",
      render: (r) => <Data>{fmt(r.affected_consumers_count, 0)}</Data>,
    },
    {
      key: "regulator_reported",
      label: "Regulator",
      render: (r) =>
        r.regulator_reported ? (
          <Badge c={C.locked} Icon={BadgeCheck} label="Regulator notified" />
        ) : (
          <Data color={C.muted}>—</Data>
        ),
    },
    {
      key: "description",
      label: "Description",
      render: (r) => (
        <span
          title={r.description || ""}
          style={{ fontFamily: SANS, fontSize: 12, color: C.body }}
        >
          {truncate(r.description)}
        </span>
      ),
    },
  ];

  const complaintCols = [
    {
      key: "product_name",
      label: "Product",
      render: (r) => <Data color={C.ink}>{r.product_name || "—"}</Data>,
    },
    {
      key: "channel",
      label: "Channel",
      render: (r) => <Data color={C.body}>{r.channel || "—"}</Data>,
    },
    {
      key: "received_date",
      label: "Received",
      render: (r) => <Data color={C.body}>{r.received_date || "—"}</Data>,
    },
    {
      key: "status",
      label: "Status",
      render: (r) => <ComplaintStatusBadge status={r.status} />,
    },
    {
      key: "description",
      label: "Description",
      render: (r) => (
        <span
          title={r.description || ""}
          style={{ fontFamily: SANS, fontSize: 12, color: C.body }}
        >
          {truncate(r.description)}
        </span>
      ),
    },
    {
      key: "_action",
      label: "Update status",
      render: (r) => {
        const edit = statusEdit[r.complaint_id] || {};
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <select
              value={edit.status || ""}
              onChange={(ev) =>
                setStatusEdit((s) => ({
                  ...s,
                  [r.complaint_id]: {
                    ...s[r.complaint_id],
                    status: ev.target.value,
                  },
                }))
              }
              style={{ ...field, width: "auto", minWidth: 120, padding: "5px 8px" }}
            >
              <option value="">Status…</option>
              {COMPLAINT_STATUSES.map((st) => (
                <option key={st} value={st}>
                  {labelize(st)}
                </option>
              ))}
            </select>
            <input
              type="date"
              title="Resolved date"
              aria-label="Resolved date"
              value={edit.resolved_date || ""}
              onChange={(ev) =>
                setStatusEdit((s) => ({
                  ...s,
                  [r.complaint_id]: {
                    ...s[r.complaint_id],
                    resolved_date: ev.target.value,
                  },
                }))
              }
              style={{ ...field, width: 140, padding: "5px 8px" }}
            />
            <Btn
              variant="secondary"
              onClick={() => submitStatus(r.complaint_id)}
              disabled={saving || !edit.status}
            >
              Save
            </Btn>
          </div>
        );
      },
    },
  ];

  const recallCols = [
    {
      key: "product_name",
      label: "Product",
      render: (r) => <Data color={C.ink}>{r.product_name || "—"}</Data>,
    },
    {
      key: "initiated_date",
      label: "Initiated",
      render: (r) => <Data color={C.body}>{r.initiated_date || "—"}</Data>,
    },
    {
      key: "reason",
      label: "Reason",
      render: (r) => (
        <span
          title={r.reason || ""}
          style={{ fontFamily: SANS, fontSize: 12, color: C.body }}
        >
          {truncate(r.reason)}
        </span>
      ),
    },
    {
      key: "units_affected",
      label: "Units",
      align: "right",
      render: (r) => <Data>{fmt(r.units_affected, 0)}</Data>,
    },
    {
      key: "incident_id",
      label: "Incident",
      render: (r) => <Data color={C.body}>{r.incident_id || "—"}</Data>,
    },
    {
      key: "completed",
      label: "Status",
      render: (r) =>
        r.completed ? (
          <Badge c={C.primary} Icon={CheckCircle2} label="Completed" />
        ) : (
          <Badge c={C.accent} Icon={Clock} label="Active" />
        ),
    },
  ];

  const tabs = [
    { id: "incidents", label: "Incidents", count: incidents.length },
    { id: "complaints", label: "Complaints", count: complaints.length },
    { id: "recalls", label: "Recalls", count: recalls.length },
  ];

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <Page>
      <PageHeader
        overline="ESRS S4 · Consumers & End-Users"
        OverlineIcon={ShoppingCart}
        title="Consumers & End-Users"
        subtitle="Product incidents, consumer complaints, and recalls affecting end-user safety and rights."
        entities={entities}
        entityId={entityId}
        onEntityChange={setEntityId}
        onRefresh={load}
        loading={loading}
      />

      <ErrorBanner message={error} onDismiss={() => setError("")} />

      {!entityId ? (
        <EmptyState
          Icon={ShoppingCart}
          title="No reporting entity selected"
          body="Choose a reporting entity to view its ESRS S4 consumer and end-user disclosures."
        />
      ) : loading && !summary ? (
        <div style={{ display: "grid", gap: 16 }}>
          <Skeleton h={92} />
          <Skeleton h={320} />
        </div>
      ) : (
        <>
          {/* KPI strip */}
          <Card style={{ marginBottom: 16 }}>
            <Overline Icon={ShoppingCart}>S4 disclosure summary</Overline>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                gap: 20,
              }}
            >
              <Metric label="Total incidents" value={fmt(kpis.totalIncidents, 0)} />
              <Metric
                label="Critical / high incidents"
                value={fmt(kpis.criticalHigh, 0)}
                color={kpis.criticalHigh > 0 ? C.destructive : C.ink}
              />
              <Metric
                label="Open complaints"
                value={fmt(kpis.openComplaints, 0)}
                color={kpis.openComplaints > 0 ? C.accent : C.ink}
              />
              <Metric
                label="Active recalls"
                value={fmt(kpis.activeRecalls, 0)}
                color={kpis.activeRecalls > 0 ? C.accent : C.ink}
              />
            </div>
          </Card>

          <TabBar tabs={tabs} active={tab} onChange={setTab} />

          {/* ─── Incidents ─────────────────────────────────────────────── */}
          {tab === "incidents" && (
            <Card>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                  marginBottom: 14,
                }}
              >
                <Overline Icon={AlertTriangle} style={{ marginBottom: 0 }}>
                  Product incidents
                </Overline>
                <div style={{ flex: 1 }} />
                <select
                  value={incType}
                  onChange={(e) => setIncType(e.target.value)}
                  aria-label="Filter incident type"
                  style={{ ...field, width: "auto", minWidth: 150 }}
                >
                  <option value="">All types</option>
                  {INCIDENT_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {labelize(t)}
                    </option>
                  ))}
                </select>
                <select
                  value={incSeverity}
                  onChange={(e) => setIncSeverity(e.target.value)}
                  aria-label="Filter severity"
                  style={{ ...field, width: "auto", minWidth: 140 }}
                >
                  <option value="">All severities</option>
                  {SEVERITIES.map((s) => (
                    <option key={s} value={s}>
                      {labelize(s)}
                    </option>
                  ))}
                </select>
              </div>

              <Collapsible
                open={incOpen}
                onToggle={() => setIncOpen((o) => !o)}
                openLabel="Record incident"
              >
                <form onSubmit={submitIncident}>
                  <div style={grid2}>
                    <div>
                      <Label>Product name *</Label>
                      <input
                        required
                        type="text"
                        value={incForm.product_name}
                        onChange={(e) =>
                          setIncForm((f) => ({ ...f, product_name: e.target.value }))
                        }
                        style={field}
                      />
                    </div>
                    <div>
                      <Label>Incident type *</Label>
                      <select
                        value={incForm.incident_type}
                        onChange={(e) =>
                          setIncForm((f) => ({ ...f, incident_type: e.target.value }))
                        }
                        style={field}
                      >
                        {INCIDENT_TYPES.map((t) => (
                          <option key={t} value={t}>
                            {labelize(t)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Severity *</Label>
                      <select
                        value={incForm.severity}
                        onChange={(e) =>
                          setIncForm((f) => ({ ...f, severity: e.target.value }))
                        }
                        style={field}
                      >
                        {SEVERITIES.map((s) => (
                          <option key={s} value={s}>
                            {labelize(s)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Occurred date *</Label>
                      <input
                        required
                        type="date"
                        value={incForm.occurred_date}
                        onChange={(e) =>
                          setIncForm((f) => ({ ...f, occurred_date: e.target.value }))
                        }
                        style={field}
                      />
                    </div>
                    <div>
                      <Label>Affected consumers</Label>
                      <input
                        type="number"
                        min="0"
                        value={incForm.affected_consumers_count}
                        onChange={(e) =>
                          setIncForm((f) => ({
                            ...f,
                            affected_consumers_count: e.target.value,
                          }))
                        }
                        style={field}
                      />
                    </div>
                  </div>
                  <label style={checkRow}>
                    <input
                      type="checkbox"
                      checked={incForm.regulator_reported}
                      onChange={(e) =>
                        setIncForm((f) => ({
                          ...f,
                          regulator_reported: e.target.checked,
                        }))
                      }
                    />
                    Reported to regulator
                  </label>
                  <div style={{ marginBottom: 12 }}>
                    <Label>Description</Label>
                    <textarea
                      rows={3}
                      value={incForm.description}
                      onChange={(e) =>
                        setIncForm((f) => ({ ...f, description: e.target.value }))
                      }
                      style={{ ...field, resize: "vertical" }}
                    />
                  </div>
                  <Btn type="submit" disabled={saving}>
                    {saving ? "Saving…" : "Record incident"}
                  </Btn>
                </form>
              </Collapsible>

              <DataTable
                columns={incidentCols}
                rows={incidents}
                keyField="incident_id"
                emptyLabel="No incidents recorded"
              />
            </Card>
          )}

          {/* ─── Complaints ────────────────────────────────────────────── */}
          {tab === "complaints" && (
            <Card>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                  marginBottom: 14,
                }}
              >
                <Overline Icon={MessageSquare} style={{ marginBottom: 0 }}>
                  Consumer complaints
                </Overline>
                <div style={{ flex: 1 }} />
                <select
                  value={cmpStatus}
                  onChange={(e) => setCmpStatus(e.target.value)}
                  aria-label="Filter complaint status"
                  style={{ ...field, width: "auto", minWidth: 150 }}
                >
                  <option value="">All statuses</option>
                  {COMPLAINT_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {labelize(s)}
                    </option>
                  ))}
                </select>
              </div>

              <Collapsible
                open={cmpOpen}
                onToggle={() => setCmpOpen((o) => !o)}
                openLabel="Record complaint"
              >
                <form onSubmit={submitComplaint}>
                  <div style={grid2}>
                    <div>
                      <Label>Channel *</Label>
                      <select
                        value={cmpForm.channel}
                        onChange={(e) =>
                          setCmpForm((f) => ({ ...f, channel: e.target.value }))
                        }
                        style={field}
                      >
                        {CHANNELS.map((c) => (
                          <option key={c} value={c}>
                            {labelize(c)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Received date *</Label>
                      <input
                        required
                        type="date"
                        value={cmpForm.received_date}
                        onChange={(e) =>
                          setCmpForm((f) => ({ ...f, received_date: e.target.value }))
                        }
                        style={field}
                      />
                    </div>
                    <div>
                      <Label>Product name</Label>
                      <input
                        type="text"
                        value={cmpForm.product_name}
                        onChange={(e) =>
                          setCmpForm((f) => ({ ...f, product_name: e.target.value }))
                        }
                        style={field}
                      />
                    </div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <Label>Description</Label>
                    <textarea
                      rows={3}
                      value={cmpForm.description}
                      onChange={(e) =>
                        setCmpForm((f) => ({ ...f, description: e.target.value }))
                      }
                      style={{ ...field, resize: "vertical" }}
                    />
                  </div>
                  <Btn type="submit" disabled={saving}>
                    {saving ? "Saving…" : "Record complaint"}
                  </Btn>
                </form>
              </Collapsible>

              <DataTable
                columns={complaintCols}
                rows={complaints}
                keyField="complaint_id"
                emptyLabel="No complaints recorded"
              />
            </Card>
          )}

          {/* ─── Recalls ───────────────────────────────────────────────── */}
          {tab === "recalls" && (
            <Card>
              <Overline Icon={Package}>Product recalls</Overline>

              <Collapsible
                open={rclOpen}
                onToggle={() => setRclOpen((o) => !o)}
                openLabel="Record recall"
              >
                <form onSubmit={submitRecall}>
                  <div style={grid2}>
                    <div>
                      <Label>Product name *</Label>
                      <input
                        required
                        type="text"
                        value={rclForm.product_name}
                        onChange={(e) =>
                          setRclForm((f) => ({ ...f, product_name: e.target.value }))
                        }
                        style={field}
                      />
                    </div>
                    <div>
                      <Label>Initiated date *</Label>
                      <input
                        required
                        type="date"
                        value={rclForm.initiated_date}
                        onChange={(e) =>
                          setRclForm((f) => ({ ...f, initiated_date: e.target.value }))
                        }
                        style={field}
                      />
                    </div>
                    <div>
                      <Label>Units affected</Label>
                      <input
                        type="number"
                        min="0"
                        value={rclForm.units_affected}
                        onChange={(e) =>
                          setRclForm((f) => ({ ...f, units_affected: e.target.value }))
                        }
                        style={field}
                      />
                    </div>
                    <div>
                      <Label>Linked incident</Label>
                      <select
                        value={rclForm.incident_id}
                        onChange={(e) =>
                          setRclForm((f) => ({ ...f, incident_id: e.target.value }))
                        }
                        style={field}
                      >
                        <option value="">None</option>
                        {incidents.map((i) => (
                          <option key={i.incident_id} value={i.incident_id}>
                            {i.product_name} · {i.incident_id}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <Label>Reason</Label>
                    <textarea
                      rows={3}
                      value={rclForm.reason}
                      onChange={(e) =>
                        setRclForm((f) => ({ ...f, reason: e.target.value }))
                      }
                      style={{ ...field, resize: "vertical" }}
                    />
                  </div>
                  <label style={checkRow}>
                    <input
                      type="checkbox"
                      checked={rclForm.completed}
                      onChange={(e) =>
                        setRclForm((f) => ({ ...f, completed: e.target.checked }))
                      }
                    />
                    Recall completed
                  </label>
                  <Btn type="submit" disabled={saving}>
                    {saving ? "Saving…" : "Record recall"}
                  </Btn>
                </form>
              </Collapsible>

              <DataTable
                columns={recallCols}
                rows={recalls}
                keyField="recall_id"
                emptyLabel="No recalls recorded"
              />
            </Card>
          )}
        </>
      )}
    </Page>
  );
}
